import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== Centralized Axios instance with retry logic =====
const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
});

// Retry interceptor: retry failed requests up to 2 times (network errors + 5xx).
// Also normalizes the FastAPI/Pydantic `detail` field to a plain string BEFORE
// the error reaches callers — toast.error(e.response.data.detail) is wired up
// in 30+ places that assume a string, and Pydantic-V2 422s return an array of
// objects ({type, loc, msg, input, url}). Passing that array to sonner causes
// it to render the objects as React children and crash with React error #31.
// Catching at the interceptor means every caller stays safe with no per-file edits.
api.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (!config) return Promise.reject(error);

  config._retryCount = config._retryCount || 0;
  const isRetryable =
    !error.response ||                          // Network error
    (error.response.status >= 500 && error.response.status < 600);  // Server error

  if (isRetryable && config._retryCount < 2 && !config._skipAuthRetry) {
    config._retryCount += 1;
    await new Promise((r) => setTimeout(r, 1000 * config._retryCount)); // backoff
    return api(config);
  }

  // Normalize non-string `detail` shapes to a plain string for safe display.
  if (error.response?.data && error.response.data.detail !== undefined) {
    const d = error.response.data.detail;
    if (typeof d !== 'string') {
      let msg = null;
      if (Array.isArray(d)) {
        const msgs = d.map(x => (x && typeof x === 'object' && x.msg) ? x.msg : (typeof x === 'string' ? x : null)).filter(Boolean);
        if (msgs.length) msg = msgs.join('; ');
      } else if (d && typeof d === 'object') {
        msg = d.msg || JSON.stringify(d);
      }
      error.response.data.detail = msg || 'Request failed';
    }
  }

  return Promise.reject(error);
});

// Default export: the configured instance (used by AuthContext)
export default api;

// ===== API helper functions =====

// Tasks
export const getTasks = (params = {}) => api.get(`${API}/tasks`, { params });
export const getTasksLightweight = (params = {}) => api.get(`${API}/tasks/list`, { params });
export const getTask = (id) => api.get(`${API}/tasks/${id}`);
export const getTaskCounts = () => api.get(`${API}/tasks/counts/summary`);
export const createTask = (data) => api.post(`${API}/tasks`, data);
export const updateTask = (id, data) => api.put(`${API}/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`${API}/tasks/${id}`);
export const assignTask = (id, designer_id) => api.post(`${API}/tasks/${id}/assign`, { designer_id });
export const assignCustomer = (id, customer_id) => api.post(`${API}/tasks/${id}/assign-customer`, { customer_id });
export const updateTaskStatus = (id, data) => api.post(`${API}/tasks/${id}/status`, data);
export const getTaskHistory = (id) => api.get(`${API}/tasks/${id}/history`);
export const exportTaskBundle = (id, selections) =>
  api.post(`${API}/tasks/${id}/export-bundle`, selections, { responseType: 'blob' });
export const getNextKsDesignNo = (clientCode) => api.get(`${API}/tasks/next-ks-design-no`, { params: { client_code: clientCode } });

// AI Instruction Parser
export const aiParseInstructions = (instruction) => api.post(`${API}/tasks/ai-parse-instructions`, { instruction });

// New Workflow Endpoints
export const salesAssignTask = (id, customer_id, design_head_id, remarks = null) =>
  api.post(`${API}/tasks/${id}/sales-assign`, { customer_id, design_head_id, remarks });
export const designerStartWork = (id) => api.post(`${API}/tasks/${id}/start-work`);
export const designerSubmitWork = (id, remarks = null) =>
  api.post(`${API}/tasks/${id}/designer-submit`, { remarks });
export const clientRequestRevision = (id, data) =>
  api.post(`${API}/tasks/${id}/client-revision`, data);

// Approval Workflow
export const cadHeadApprove = (id) => api.post(`${API}/tasks/${id}/cad-head-approve`);
export const designHeadApprove = (id) => api.post(`${API}/tasks/${id}/design-head-approve`);
export const sendForClientApproval = (id) => api.post(`${API}/tasks/${id}/send-for-client-approval`);
export const clientApprove = (id) => api.post(`${API}/tasks/${id}/client-approve`);

// Users
export const getUsers = () => api.get(`${API}/users`);
export const getPendingUsers = () => api.get(`${API}/users/pending`);
export const getDesigners = () => api.get(`${API}/users/designers`);
export const getExecutors = () => api.get(`${API}/users/executors`);
export const getDesignHeads = () => api.get(`${API}/users/design-heads`);
export const getCustomers = () => api.get(`${API}/users/customers`);
export const createUser = (data) => api.post(`${API}/users`, data);
export const updateUser = (id, data) => api.put(`${API}/users/${id}`, data);
export const deleteUser = (id) => api.delete(`${API}/users/${id}`);
export const approveUser = (id) => api.post(`${API}/users/${id}/approve`);
export const rejectUser = (id) => api.post(`${API}/users/${id}/reject`);
export const suspendUser = (id) => api.post(`${API}/users/${id}/suspend`);

// Auth
export const forgotPassword = (email) => api.post(`${API}/auth/forgot-password`, { email });
export const resetPassword = (token, new_password) => api.post(`${API}/auth/reset-password`, { token, new_password });

// Analytics
export const getAnalytics = () => api.get(`${API}/analytics`);
export const getCadAnalytics = () => api.get(`${API}/analytics/cad`);

// Audit Logs
export const getAuditLogs = (params = {}) => api.get(`${API}/audit-logs`, { params });

// Messages
export const getMessages = (taskId, search = null) => {
  const params = search ? { search } : {};
  return api.get(`${API}/tasks/${taskId}/messages`, { params });
};
export const sendMessage = (taskId, message, attachments = []) =>
  api.post(`${API}/tasks/${taskId}/messages`, { task_id: taskId, message, attachments });

// Upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/upload-file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Helper to get full image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
  return `${BACKEND_URL}${imagePath}`;
};

// Normalize an axios error into a plain-string message suitable for toast.error
// or inline display. FastAPI's `detail` can be a string, an object, or an array
// of pydantic validation errors ({type, loc, msg, input, url}). Passing the raw
// array to toast.error() causes sonner to render the objects as React children
// and trigger "Objects are not valid as a React child" (React error #31).
export function formatApiError(e, fallback = 'Something went wrong') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const msgs = d.map(x => (x && typeof x === 'object' && x.msg) ? x.msg : (typeof x === 'string' ? x : null))
                  .filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (d && typeof d === 'object' && d.msg) return d.msg;
  return e?.message || fallback;
}

// Stone Template — fetched as a blob via Bearer auth (no ?token= in URL).
// Returns nothing; triggers a browser download as a side-effect.
export const downloadStoneTemplate = async () => {
  const response = await api.get(`${API}/templates/stone-entry`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'stone_entry_template.xlsx');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const uploadStoneData = (taskId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/tasks/${taskId}/upload-stones`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// QC Checklist
export const updateQCChecklist = (taskId, data) => api.post(`${API}/tasks/${taskId}/qc-checklist`, data);
export const getQCStatus = (taskId) => api.get(`${API}/tasks/${taskId}/qc-status`);

// Quotation Engine
export const getLaborRates = (params = {}) => api.get(`${API}/quotation/labor-rates`, { params });
export const createLaborRate = (data) => api.post(`${API}/quotation/labor-rates`, data);
export const updateLaborRate = (id, data) => api.put(`${API}/quotation/labor-rates/${id}`, data);
export const deleteLaborRate = (id) => api.delete(`${API}/quotation/labor-rates/${id}`);
export const importLaborRates = (file, replaceExisting = false) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/quotation/import-labor-rates?replace_existing=${replaceExisting}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getProcessCodes = () => api.get(`${API}/quotation/process-codes`);
export const getItemCodes = (processCode = null) => {
  const params = processCode ? { process_code: processCode } : {};
  return api.get(`${API}/quotation/item-codes`, { params });
};
export const calculateQuotation = (data) => api.post(`${API}/quotation/calculate`, data);
export const getQuotations = (params = {}) => api.get(`${API}/quotation/quotations`, { params });
export const getQuotation = (id) => api.get(`${API}/quotation/quotations/${id}`);
export const deleteQuotation = (id) => api.delete(`${API}/quotation/quotations/${id}`);
// Bearer-auth blob download — no ?token= in URL. Returns nothing; triggers
// a browser download as a side-effect.
export const exportQuotation = async (id, format = 'excel') => {
  const response = await api.get(`${API}/quotation/export/${id}`, {
    params: { format },
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = match ? match[1] : `quotation_${id}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Simplified Workflow
export const moveToQC = (id) => api.post(`${API}/tasks/${id}/move-to-qc`);
export const sendToClient = (id) => api.post(`${API}/tasks/${id}/send-to-client`);
export const approveTask = (id) => api.post(`${API}/tasks/${id}/approve`);
// In-place revision request (appends to client_revisions, no clone)
export const requestRevisionInPlace = (id, data) => api.post(`${API}/tasks/${id}/request-revision`, data);
// Backwards-compat alias used across the app
export const requestRevisionClone = requestRevisionInPlace;
export const getTaskRevisions = (id) => api.get(`${API}/tasks/${id}/revisions`);

// Work timer (play/pause)
export const startTaskTimer = (id) => api.post(`${API}/tasks/${id}/timer/start`);
export const pauseTaskTimer = (id) => api.post(`${API}/tasks/${id}/timer/pause`);
export const resetTaskTimer = (id) => api.post(`${API}/tasks/${id}/timer/reset`);

// Client Account Management
export const createClientAccount = (data) => api.post(`${API}/users/create-client`, data);

// Client Masters
export const getClientMasters = () => api.get(`${API}/client-masters`);
export const getClientMaster = (id) => api.get(`${API}/client-masters/${id}`);
export const createClientMaster = (data) => api.post(`${API}/client-masters`, data);
export const updateClientMaster = (id, data) => api.put(`${API}/client-masters/${id}`, data);
export const deleteClientMaster = (id) => api.delete(`${API}/client-masters/${id}`);

// Enhanced Quotations
export const saveFullQuotation = (data) => api.post(`${API}/quotation/save-full`, data);
export const getSavedQuotations = (params = {}) => api.get(`${API}/quotation/saved`, { params });
export const getSavedQuotation = (id) => api.get(`${API}/quotation/saved/${id}`);
export const deleteSavedQuotation = (id) => api.delete(`${API}/quotation/saved/${id}`);
export const linkQuotationToTask = (id, payload) => api.patch(`${API}/quotation/saved/${id}/link-task`, payload);

// Style No lookups
export const getTaskByStyleNo = (styleNo) => api.get(`${API}/tasks/by-style/${encodeURIComponent(styleNo)}`);
export const getQuotationsByStyleNo = (styleNo) => api.get(`${API}/quotations/by-style/${encodeURIComponent(styleNo)}`);

// Sync CAD sheet data back to task
export const syncCadToTask = (taskId, cadData) => api.put(`${API}/tasks/${taskId}`, cadData);

// Diamond Master Data
export const getDiamondRates = (params = {}) => api.get(`${API}/quotation/diamond-rates`, { params });
export const getDiamondShapes = () => api.get(`${API}/quotation/diamond-shapes`);
export const getDiamondQualities = (shape) => api.get(`${API}/quotation/diamond-qualities`, { params: { shape } });
export const diamondRateLookup = (shape, rm_code, carat_weight) =>
  api.get(`${API}/quotation/diamond-rate-lookup`, { params: { shape, rm_code, carat_weight } });
export const settingRateLookup = (stone_shape, setting_type, weight_ct = 0) =>
  api.get(`${API}/quotation/setting-rate-lookup`, { params: { stone_shape, setting_type, weight_ct } });
export const getPointerLookup = (mm_size, shape = 'Round') =>
  api.get(`${API}/quotation/pointer-lookup`, { params: { mm_size, shape } });
export const getPointerTable = (shape = 'Round') =>
  api.get(`${API}/quotation/pointer-table`, { params: { shape } });
export const getMasterSummary = () => api.get(`${API}/quotation/master-summary`);

// CAD Sheet CRUD
export const createCadSheet = (data) => api.post(`${API}/cad-sheets`, data);
export const listCadSheets = (params = {}) => api.get(`${API}/cad-sheets`, { params });
export const getCadSheet = (id) => api.get(`${API}/cad-sheets/${id}`);
export const updateCadSheet = (id, data) => api.put(`${API}/cad-sheets/${id}`, data);
export const deleteCadSheet = (id) => api.delete(`${API}/cad-sheets/${id}`);
export const bulkDeleteCadSheets = (ids) => api.delete(`${API}/cad-sheets`, { data: ids });
export const parseStonesScreenshot = (image_base64, mime_type = 'image/jpeg') =>
  api.post(`${API}/cad-sheets/parse-stones-screenshot`, { image_base64, mime_type });

// Portal Import (NEW SGI flow - matches backend/routes/portal_import.py)
export const getPortals = () => api.get(`${API}/portals`);
export const createPortal = (data) => api.post(`${API}/portals`, data);
export const updatePortal = (id, data) => api.put(`${API}/portals/${id}`, data);
export const deletePortal = (id) => api.delete(`${API}/portals/${id}`);
// Portal scrape calls are slow — list paginates across the portal and
// detail scrape batches 3-at-a-time with per-order retries + optional LLM
// enrichment (up to 45s per order). The default 30s axios timeout was
// killing imports >~10 orders before the backend finished. Nginx allows
// 180s (see scripts/nginx-api.conf), so cap the client just under that.
// _skipAuthRetry opts these out of the global retry interceptor — at this
// timeout, retrying on timeout would cost minutes and re-hammer SGI.
const PORTAL_LONG = { timeout: 170000, _skipAuthRetry: true };
// Step 1: fetch open orders list
export const fetchPortalOrders = (data) => api.post(`${API}/portals/fetch-orders`, data, PORTAL_LONG);
// Step 2: scrape selected order details (with AI parsing)
export const scrapePortalDetails = (data) => api.post(`${API}/portals/scrape-details`, data, PORTAL_LONG);
// Step 3: confirm and create tasks
export const confirmPortalImport = (data) => api.post(`${API}/portals/confirm-import`, data, PORTAL_LONG);
export const getPortalImports = () => api.get(`${API}/portals/imports`);
// Reverse upload: push CAD images (consolidated + per-view PNGs) from a task
// back to the SGI portal as CAD images on the source order.
//   data: { task_id, images: [{ data_url, filename, notes? }], portal_id?, order_no? }
export const pushCADToPortal = (data) => api.post(`${API}/portals/upload-cad`, data, PORTAL_LONG);

// Cached customer list
let _customerCache = null;
let _customerCacheTime = 0;
const CUSTOMER_CACHE_TTL = 5 * 60 * 1000;

export const getCustomersCached = async () => {
  const now = Date.now();
  if (_customerCache && (now - _customerCacheTime) < CUSTOMER_CACHE_TTL) return _customerCache;
  const res = await api.get(`${API}/customers`);
  _customerCache = res.data;
  _customerCacheTime = now;
  return _customerCache;
};

export const invalidateCustomerCache = () => { _customerCache = null; };

// ===== Stone Procurement (RFQ portal) =====
export const createStoneSupplier = (data) => api.post(`${API}/stones/suppliers`, data);
export const listStoneSuppliers = () => api.get(`${API}/stones/suppliers`);
export const updateStoneSupplier = (id, data) => api.patch(`${API}/stones/suppliers/${id}`, data);
export const deactivateStoneSupplier = (id) => api.delete(`${API}/stones/suppliers/${id}`);

export const createStoneRequirement = (data) => api.post(`${API}/stones/requirements`, data);
export const listStoneRequirements = (status) =>
  api.get(`${API}/stones/requirements`, { params: status ? { status } : {} });
export const getStoneRequirement = (id) => api.get(`${API}/stones/requirements/${id}`);
export const updateStoneRequirement = (id, data) => api.patch(`${API}/stones/requirements/${id}`, data);
export const deleteStoneRequirement = (id) => api.delete(`${API}/stones/requirements/${id}`);

export const submitStoneQuote = (data) => api.post(`${API}/stones/quotes`, data);
export const listStoneQuotes = (requirementId) =>
  api.get(`${API}/stones/quotes`, { params: requirementId ? { requirement_id: requirementId } : {} });
export const updateStoneQuote = (id, data) => api.patch(`${API}/stones/quotes/${id}`, data);
export const acceptStoneQuote = (id) => api.post(`${API}/stones/quotes/${id}/accept`);
export const rejectStoneQuote = (id) => api.post(`${API}/stones/quotes/${id}/reject`);
export const requestStoneQuoteRevision = (id, data) =>
  api.post(`${API}/stones/quotes/${id}/request-revision`, data);

export const listStoneQuoteMessages = (quoteId) => api.get(`${API}/stones/quotes/${quoteId}/messages`);
export const postStoneQuoteMessage = (quoteId, body) =>
  api.post(`${API}/stones/quotes/${quoteId}/messages`, { body });
